import { isCommentAnnotation } from './utils'

export class CommentEntity {
  id: number

  recipients: any[]

  raw: any

  constructor(data: any) {
    this.raw = data || {}
    this.id = Number(data?.id || 0)
    const commentObj =
      data?.comment_object || data?.commentObject || data?.comment
    this.recipients =
      commentObj?.recipients || data?.recipients || data?.recipient_ids || []
  }

  /**
   * Casts raw data or object to CommentEntity instance
   */
  static from(data: any): CommentEntity {
    if (data instanceof CommentEntity) return data
    return new CommentEntity(data)
  }

  /**
   * Filters recipient IDs against active building user list
   */
  getValidRecipients(userList?: any[]): any[] {
    const base = Array.isArray(this.recipients) ? this.recipients : []
    if (!Array.isArray(userList) || userList.length === 0) {
      return base
    }

    const validUserIds = new Set(
      userList
        .map((u: any) => Number(u?.id || u?.user_id))
        .filter((id) => !Number.isNaN(id) && id > 0)
    )

    return base.filter((r: any) => {
      const rId =
        typeof r === 'number'
          ? r
          : Number(r?.recipient_id || r?.recipientId || r?.id || r)
      return validUserIds.has(rId)
    })
  }

  /**
   * Checks if valid recipients match all active building users
   */
  isAllRecipients(userList?: any[]): boolean {
    const valid = this.getValidRecipients(userList)
    if (
      !Array.isArray(userList) ||
      userList.length === 0 ||
      valid.length === 0
    ) {
      return false
    }

    const validUserIds = new Set(
      valid
        .map((r: any) =>
          typeof r === 'number'
            ? r
            : Number(r?.recipient_id || r?.recipientId || r?.id || r)
        )
        .filter((id) => !Number.isNaN(id) && id > 0)
    )

    const totalBuildingUserIds = new Set(
      userList
        .map((u: any) => Number(u?.id || u?.user_id))
        .filter((id) => !Number.isNaN(id) && id > 0)
    )

    return (
      totalBuildingUserIds.size > 0 &&
      validUserIds.size === totalBuildingUserIds.size
    )
  }

  /**
   * Returns recipient display metadata (badge text, color, labels)
   */
  getRecipientDisplayInfo(userList?: any[]) {
    const isAll = this.isAllRecipients(userList)
    const valid = this.getValidRecipients(userList)
    const count = valid.length
    const total = userList?.length || 0

    return {
      isAllRecipients: isAll,
      recipientCount: count,
      badgeText: isAll ? '全' : String(count),
      badgeColor: isAll ? 'rgb(22, 163, 74)' : 'rgb(14, 165, 233)',
      buttonText: `公開先 ${isAll ? '全員' : `${count}名`}`,
      modalTitle: `公開先 (${isAll ? '全員' : `${count}名`})`,
      formTagText: isAll ? '全員' : `${count} / ${total} 名`,
      formTagColor: isAll ? 'green' : 'blue',
      tooltipTitle: isAll ? '公開先：全員' : `公開先：${count}名`
    }
  }

  /**
   * Evaluates if the given user has permission to view the comment:
   * 1. SA (System Admin) sees all comments
   * 2. Author can view their own comment
   * 3. If recipients field is empty/legacy data for non-RN comments, anyone can view
   * 4. Otherwise, user must be in valid recipients
   */
  canUserView(
    currentUser?: { id?: number | string; role?: string } | null,
    userList?: any[]
  ): boolean {
    const isRNComment = isCommentAnnotation(this.raw)

    if (!currentUser) {
      return !isRNComment
    }

    const roleStr = String(currentUser.role || '').toUpperCase()
    if (roleStr === 'SA' || roleStr === 'SYSTEMADMIN') {
      return true
    }

    const currentUserId = Number(currentUser.id)
    if (Number.isNaN(currentUserId) || currentUserId <= 0) {
      return !isRNComment
    }

    const commentObj =
      this.raw?.comment_object || this.raw?.commentObject || this.raw?.comment
    const authorId = Number(
      commentObj?.author_id ||
        commentObj?.authorId ||
        commentObj?.created_by ||
        this.raw?.author_id ||
        this.raw?.authorId ||
        this.raw?.created_by ||
        0
    )
    if (authorId > 0 && authorId === currentUserId) {
      return true
    }

    if (!Array.isArray(this.recipients) || this.recipients.length === 0) {
      if (!isCommentAnnotation(this.raw)) {
        return true
      }
      return authorId > 0 && authorId === currentUserId
    }

    if (this.isAllRecipients(userList)) {
      return true
    }

    const validRecipients = this.getValidRecipients(userList)
    if (!validRecipients || validRecipients.length === 0) {
      return false
    }

    return validRecipients.some((r: any) => {
      const rId =
        typeof r === 'number'
          ? r
          : Number(r?.recipient_id || r?.recipientId || r?.id || r)
      return rId === currentUserId
    })
  }
}

export default CommentEntity
